import i18next from 'i18next';
import { DateTime } from 'luxon';

import dateFormatter from '../formatters/dateFormatter';

import { MEDIUM_DATE_TIME, SHORT_TIME, TIMEZONE } from '../constants/dates';
import { parseDateTime } from 'async-timing';

function formatDateTimes(
  start: DateTime,
  end: DateTime,
  timeZone?: string,
  locale?: string
) {
  return {
    startTime: dateFormatter(start, SHORT_TIME, timeZone, locale),
    endTime: dateFormatter(end, SHORT_TIME, timeZone, locale),
    startDateTime: dateFormatter(start, MEDIUM_DATE_TIME, timeZone, locale),
    endDateTime: dateFormatter(end, MEDIUM_DATE_TIME, timeZone, locale),
  };
}

interface FormattedTimes {
  startTime: string;
  endTime: string;
  startDateTime: string;
  endDateTime: string;
}

function formatSameDateInteraction(
  times: { startDateTime: string; startTime: string },
  timeZoneDisplay: string,
  isToday: boolean,
  translator: (key: string, params?: any) => string
): string {
  if (isToday) {
    return translator('Today {{startTime}} {{timeZoneDisplay}}', {
      startTime: times.startTime,
      timeZoneDisplay,
    });
  }

  return `${times.startDateTime} ${timeZoneDisplay}`;
}

function formatTodayInteraction(
  times: { startTime: string; endTime: string },
  timeZoneDisplay: string,
  translator: (key: string, params?: any) => string
): string {
  return translator('Today {{startTime}} to {{endTime}} {{timeZoneDisplay}}', {
    startTime: times.startTime,
    endTime: times.endTime,
    timeZoneDisplay,
  });
}

function buildDateRangeString(
  times: FormattedTimes,
  timeZoneDisplay: string,
  sameDay: boolean
): string {
  if (sameDay) {
    return `${times.startDateTime} - ${times.endTime} ${timeZoneDisplay}`;
  }

  return `${times.startDateTime} - ${times.endDateTime} ${timeZoneDisplay}`;
}

export function getFriendlyInteractionDate(
  interaction:
    | {
        startDate: string | Date | DateTime | null;
        endDate: string | Date | DateTime | null;
      }
    | undefined
    | null,
  timeZone?: string,
  translator: (key: string, params?: any) => string = i18next.t,
  locale?: string
): string {
  if (!interaction || !interaction?.startDate || !interaction?.endDate) {
    return '';
  }

  const start = parseDateTime(interaction.startDate, timeZone);
  const end = parseDateTime(interaction.endDate, timeZone);

  if (!start || !end) {
    return '';
  }

  const today = DateTime.local();
  const differentTimeZones = start.offset !== today.offset;
  const timeZoneDisplay = differentTimeZones ? start.toFormat(TIMEZONE) : '';

  const times = formatDateTimes(start, end, timeZone, locale);
  const isStartToday = start.hasSame(today, 'day');
  const isEndToday = end.hasSame(today, 'day');
  const sameDay = start.hasSame(end, 'day');

  // if interaction.startDate and interaction.endDate were set to be the same, only return startDateTime string:
  if (times.startDateTime === times.endDateTime) {
    return formatSameDateInteraction(times, timeZoneDisplay, isStartToday, translator);
  }

  // if it is today, show a time
  if (isStartToday && isEndToday) {
    return formatTodayInteraction(times, timeZoneDisplay, translator);
  }

  return buildDateRangeString(times, timeZoneDisplay, sameDay);
}
